/**
 * Editable controls for the "Dynamic block parameters" section in the
 * Inspector sidebar (Variant A — DYN-UX-A).
 *
 * Each component renders one typed `<input>` / `<select>` per
 * parameter kind and commits the user's edit via the caller-supplied
 * `onCommit(rawValue)` callback. The caller is expected to wrap the
 * commit into a `dynamic/set` dispatch against the selected INSERT
 * (see `DynamicBlockParametersSection` in InspectorSidebar.tsx).
 *
 * Commit strategy (AutoCAD parity — the Properties palette in
 * AutoCAD commits text/number edits on blur/Enter, toggles and
 * dropdowns commit immediately):
 *
 *   ─────────────────┬────────────────────────────────────────────
 *   kind             │ commit trigger
 *   ─────────────────┼────────────────────────────────────────────
 *   flip (checkbox)  │ onChange — immediate
 *   visibility/lookup│ onChange — immediate
 *   distance (number)│ onBlur + Enter (Escape reverts the draft)
 *   angle (number)   │ onBlur + Enter (Escape reverts the draft)
 *   unknown (text)   │ onBlur + Enter (Escape reverts the draft)
 *   ─────────────────┴────────────────────────────────────────────
 *
 * The "draft" buffer for number/text inputs is required because a
 * per-keystroke dispatch would re-parse via zod, rebuild the entire
 * DrawingDocument immutable tree, and spam the audit log (see
 * dynamicBlockMutations.ts::setDynamicParameter which reconstructs
 * the full `spaces` array on every call).
 *
 * Typed-metadata fallback (real AutoCAD output from the raw-groups
 * bridge — see commit 8ab89de): when `paramDef` is undefined we
 * infer the control from the JS type of the current value:
 *   number  → distance-like number input (no unit suffix)
 *   boolean → flip checkbox
 *   string  → plain text input
 *
 * Without typed metadata we cannot offer a choice list, so the
 * string fallback is an `<input>` rather than a `<select>`; the
 * reducer's zod validation in `dynamic/set` still guards against
 * numeric NaNs.
 */

import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
} from "react";

// Compound controls (polar / xy / point / basePoint) live in a sibling
// module so this file stays under the 500-line CLAUDE.md threshold.
import {
  DynPolarControl,
  DynXYControl,
  DynPointControl,
} from "./dynamicBlockCompoundControls";

// Shared widget types live in `dynamicBlockControlTypes.ts` so siblings
// can pull them in without re-importing this module (was a depcruise
// cycle source).
export type {
  DynControlKind,
  DynNumericBounds,
  DynParamDescriptor,
  DynControlProps,
} from "./dynamicBlockControlTypes";

import type {
  DynControlProps,
  DynParamDescriptor,
} from "./dynamicBlockControlTypes";

// ═══════════════════════════════════════════════════════════════════════
// Checkbox (flip)
// ═══════════════════════════════════════════════════════════════════════

function DynCheckboxControl({
  param,
  onCommit,
  editAriaLabel,
}: DynControlProps): ReactElement {
  const checked = Boolean(param.value);
  return (
    <input
      type="checkbox"
      className="property-input"
      checked={checked}
      aria-label={editAriaLabel}
      data-testid={`dyn-param-${param.name}-checkbox`}
      onChange={(event) =>
        onCommit(event.target.checked ? "true" : "false")
      }
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Select (visibility / lookup)
// ═══════════════════════════════════════════════════════════════════════

function DynSelectControl({
  param,
  onCommit,
  editAriaLabel,
}: DynControlProps): ReactElement {
  const current = String(param.value);
  const choices: readonly string[] =
    param.choices && param.choices.length > 0
      ? param.choices
      : [current];
  // Include the current value even if the metadata's choice list
  // somehow omits it — the user should never see a <select> with no
  // matching <option>, which would render as empty in the browser.
  const options = choices.includes(current)
    ? choices
    : [current, ...choices];
  return (
    <select
      className="property-input"
      value={current}
      aria-label={editAriaLabel}
      data-testid={`dyn-param-${param.name}-select`}
      onChange={(event) => onCommit(event.target.value)}
    >
      {options.map((choice) => (
        <option key={choice} value={choice}>
          {choice}
        </option>
      ))}
    </select>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Number (distance / angle) + text (unknown)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Shared blur-commit input with Escape revert. Extracted to share the
 * draft-buffer behaviour between distance/angle (number) and
 * unknown-kind (text) controls — all three commit on blur or Enter,
 * revert on Escape.
 */
function DraftCommitInput({
  param,
  onCommit,
  editAriaLabel,
  inputType,
  unitSuffix,
  testId,
}: DynControlProps & {
  inputType: "number" | "text";
  unitSuffix?: string;
  testId: string;
}): ReactElement {
  const initialValue = String(param.value);
  const [draft, setDraft] = useState(initialValue);
  const lastCommittedRef = useRef(initialValue);

  // Re-sync the draft buffer when the reducer echoes back a new value
  // after our own dispatch OR when the selection changes to a different
  // parameter. Comparing against `lastCommittedRef` avoids clobbering
  // the draft while the user is mid-edit.
  useEffect(() => {
    if (initialValue !== lastCommittedRef.current) {
      lastCommittedRef.current = initialValue;
      setDraft(initialValue);
    }
  }, [initialValue]);

  function commit() {
    if (draft === lastCommittedRef.current) return;
    lastCommittedRef.current = draft;
    onCommit(draft);
  }

  function handleKey(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      commit();
      (event.target as HTMLInputElement).blur();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setDraft(lastCommittedRef.current);
      (event.target as HTMLInputElement).blur();
    }
  }

  const input = (
    <input
      type={inputType}
      step={inputType === "number" ? "any" : undefined}
      className="property-input"
      value={draft}
      aria-label={editAriaLabel}
      data-testid={testId}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={handleKey}
    />
  );

  if (!unitSuffix) return input;
  return (
    <span className="dyn-param-input-group">
      {input}
      <span
        className="dyn-param-unit"
        data-testid={`dyn-param-${param.name}-unit`}
      >
        {unitSuffix}
      </span>
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Slider-augmented numeric (D6)
// ═══════════════════════════════════════════════════════════════════════
//
// AutoCAD's Properties palette shows a drag handle for numeric dynamic-
// block parameters. We approximate with a native `<input type="range">`
// next to the numeric input, kept in sync. The range's `min`/`max`/`step`
// come from the descriptor's `bounds.valueSet` when available; otherwise
// we fall back to `0 … 2×currentValue` (sensible default for length
// drivers like "Двутавр L"). For `mode === "list"` the widget switches
// to a `<select>` over the discretised list values.

function DynListSelect({
  param,
  onCommit,
  editAriaLabel,
  listValues,
  unitSuffix,
  testId,
}: DynControlProps & {
  listValues: readonly number[];
  unitSuffix?: string;
  testId: string;
}): ReactElement {
  const current = String(param.value);
  const options = listValues.includes(Number(current))
    ? listValues
    : [Number(current), ...listValues];
  const select = (
    <select
      className="property-input"
      value={current}
      aria-label={editAriaLabel}
      data-testid={testId}
      onChange={(event) => onCommit(event.target.value)}
    >
      {options.map((v) => (
        <option key={v} value={String(v)}>
          {v}
          {unitSuffix ? ` ${unitSuffix}` : ""}
        </option>
      ))}
    </select>
  );
  return select;
}

function DynSliderNumber({
  param,
  onCommit,
  editAriaLabel,
  unitSuffix,
  testIdInput,
  testIdSlider,
  rangeMin,
  rangeMax,
  rangeStep,
}: DynControlProps & {
  unitSuffix?: string;
  testIdInput: string;
  testIdSlider: string;
  rangeMin: number;
  rangeMax: number;
  rangeStep: number;
}): ReactElement {
  const initialValue = String(param.value);
  const [draft, setDraft] = useState(initialValue);
  const lastCommittedRef = useRef(initialValue);

  useEffect(() => {
    if (initialValue !== lastCommittedRef.current) {
      lastCommittedRef.current = initialValue;
      setDraft(initialValue);
    }
  }, [initialValue]);

  function commit(nextRaw: string): void {
    if (nextRaw === lastCommittedRef.current) return;
    lastCommittedRef.current = nextRaw;
    onCommit(nextRaw);
  }

  function handleNumBlur(): void {
    commit(draft);
  }

  function handleKey(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Enter") {
      event.preventDefault();
      commit(draft);
      (event.target as HTMLInputElement).blur();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setDraft(lastCommittedRef.current);
      (event.target as HTMLInputElement).blur();
    }
  }

  return (
    <span
      className="dyn-param-slider-group"
      data-testid={`dyn-param-${param.name}-slider-group`}
    >
      <input
        type="range"
        className="property-input dyn-param-slider"
        min={rangeMin}
        max={rangeMax}
        step={rangeStep}
        value={draft === "" ? rangeMin : draft}
        aria-label={`${editAriaLabel} — slider`}
        data-testid={testIdSlider}
        onChange={(event) => {
          // Slider drags commit immediately — matches AutoCAD grip
          // drag semantics (live preview on every pixel of drag).
          setDraft(event.target.value);
          commit(event.target.value);
        }}
      />
      <input
        type="number"
        step="any"
        className="property-input"
        value={draft}
        aria-label={editAriaLabel}
        data-testid={testIdInput}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={handleNumBlur}
        onKeyDown={handleKey}
      />
      {unitSuffix ? (
        <span
          className="dyn-param-unit"
          data-testid={`dyn-param-${param.name}-unit`}
        >
          {unitSuffix}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Compute slider range + step from the descriptor's bounds. When
 * `mode === "list"` the caller should route to `DynListSelect` and not
 * build a range. Falls back to `0 … 2×currentValue` when bounds are
 * absent.
 */
function computeRange(
  param: DynParamDescriptor,
  kind: "linear" | "rotation",
): { min: number; max: number; step: number } {
  const bounds = param.bounds;
  const current = Number(param.value);
  if (kind === "rotation") {
    // Angle sliders default to 0…360° with 1° step; honour explicit
    // min/max when available.
    return {
      min: bounds?.min ?? 0,
      max: bounds?.max ?? 360,
      step: bounds?.increment && bounds.increment > 0 ? bounds.increment : 1,
    };
  }
  const min = bounds?.min ?? 0;
  const fallbackMax = Math.max(current * 2, current + 100, 100);
  const max = bounds?.max ?? fallbackMax;
  const step =
    bounds?.mode === "increment" && bounds.increment && bounds.increment > 0
      ? bounds.increment
      : Math.max((max - min) / 100, 1);
  return { min, max, step };
}

function DynNumberControl(props: DynControlProps): ReactElement {
  const bounds = props.param.bounds;
  if (
    bounds?.mode === "list" &&
    bounds.listValues &&
    bounds.listValues.length > 0
  ) {
    return (
      <DynListSelect
        {...props}
        listValues={bounds.listValues}
        {...(props.unitMmLabel !== undefined
          ? { unitSuffix: props.unitMmLabel }
          : {})}
        testId={`dyn-param-${props.param.name}-list`}
      />
    );
  }
  const range = computeRange(props.param, "linear");
  return (
    <DynSliderNumber
      {...props}
      {...(props.unitMmLabel !== undefined
        ? { unitSuffix: props.unitMmLabel }
        : {})}
      testIdInput={`dyn-param-${props.param.name}-number`}
      testIdSlider={`dyn-param-${props.param.name}-slider`}
      rangeMin={range.min}
      rangeMax={range.max}
      rangeStep={range.step}
    />
  );
}

function DynAngleControl(props: DynControlProps): ReactElement {
  const bounds = props.param.bounds;
  if (
    bounds?.mode === "list" &&
    bounds.listValues &&
    bounds.listValues.length > 0
  ) {
    return (
      <DynListSelect
        {...props}
        listValues={bounds.listValues}
        {...(props.unitDegLabel !== undefined
          ? { unitSuffix: props.unitDegLabel }
          : {})}
        testId={`dyn-param-${props.param.name}-list`}
      />
    );
  }
  const range = computeRange(props.param, "rotation");
  return (
    <DynSliderNumber
      {...props}
      {...(props.unitDegLabel !== undefined
        ? { unitSuffix: props.unitDegLabel }
        : {})}
      testIdInput={`dyn-param-${props.param.name}-angle`}
      testIdSlider={`dyn-param-${props.param.name}-angle-slider`}
      rangeMin={range.min}
      rangeMax={range.max}
      rangeStep={range.step}
    />
  );
}

function DynTextControl(props: DynControlProps): ReactElement {
  return (
    <DraftCommitInput
      {...props}
      inputType="text"
      testId={`dyn-param-${props.param.name}-text`}
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Dispatcher — picks the right control for the param.kind
// ═══════════════════════════════════════════════════════════════════════

export function DynParamControl(props: DynControlProps): ReactElement {
  switch (props.param.kind) {
    case "flip":
      return <DynCheckboxControl {...props} />;
    case "visibility":
    case "lookup":
      return <DynSelectControl {...props} />;
    case "distance":
      return <DynNumberControl {...props} />;
    case "angle":
      return <DynAngleControl {...props} />;
    case "polar":
      return <DynPolarControl {...props} />;
    case "xy":
      return <DynXYControl {...props} />;
    case "point":
    case "basePoint":
      return <DynPointControl {...props} />;
    default:
      return <DynTextControl {...props} />;
  }
}
